function cartDrawerIsIos() {
  return /iP(hone|od|ad)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

class CartDrawerV2 extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawerV2-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (cartDrawerIsIos()) this.classList.add('is-ios');
    this.bindIosScrollbar();
    this.bindPairWellSliders();
    if (window.cartDrawerV2Watch) window.cartDrawerV2Watch(this);
  }

  bindPairWellSliders() {
    this.querySelectorAll('[data-pww-slider]').forEach((root) => {
      if (root.dataset.pwwBound) return;
      const track = root.querySelector('.cart-drawer__pair-well-with-track');
      const prev = root.querySelector('.cart-drawer__pair-well-with-arrow--prev');
      const next = root.querySelector('.cart-drawer__pair-well-with-arrow--next');
      if (!track) return;
      root.dataset.pwwBound = 'true';

      const slideSize = () => {
        const slide = track.querySelector('.cart-drawer__pair-well-with-slide');
        if (!slide) return track.clientWidth * 0.78;
        return slide.getBoundingClientRect().width + 8;
      };

      const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

      const updateArrows = () => {
        const max = maxScroll();
        if (max <= 4) {
          if (prev) prev.hidden = true;
          if (next) next.hidden = true;
          return;
        }
        if (prev) prev.hidden = track.scrollLeft <= 4;
        if (next) next.hidden = track.scrollLeft >= max - 4;
      };

      const go = (dir) => {
        track.scrollBy({ left: dir * slideSize(), behavior: 'smooth' });
      };

      const slides = () => [...track.querySelectorAll('.cart-drawer__pair-well-with-slide')];

      const isPeeking = (slide) => {
        const trackRect = track.getBoundingClientRect();
        const rect = slide.getBoundingClientRect();
        const visible = Math.min(rect.right, trackRect.right) - Math.max(rect.left, trackRect.left);
        return visible < rect.width * 0.85;
      };

      const scrollSlideIntoView = (slide) => {
        const index = slides().indexOf(slide);
        if (index < 0) return;
        track.scrollTo({ left: index * slideSize(), behavior: 'smooth' });
      };

      track.addEventListener('click', (event) => {
        if (event.target.closest('button, form, .cart-drawer__pair-well-with-item-button')) return;
        const slide = event.target.closest('.cart-drawer__pair-well-with-slide');
        if (!slide || !isPeeking(slide)) return;
        event.preventDefault();
        event.stopPropagation();
        scrollSlideIntoView(slide);
      }, true);

      if (next) {
        next.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          go(1);
        });
      }
      if (prev) {
        prev.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          go(-1);
        });
      }

      track.addEventListener('scroll', updateArrows, { passive: true });
      if (typeof ResizeObserver !== 'undefined') new ResizeObserver(updateArrows).observe(track);
      updateArrows();

      let startX = 0;
      let startY = 0;
      let startScroll = 0;
      let dragging = false;
      let moved = false;
      let axis = null;

      const onStart = (x, y) => {
        startX = x;
        startY = y;
        startScroll = track.scrollLeft;
        dragging = true;
        moved = false;
        axis = null;
      };

      const onMove = (x, y, event) => {
        if (!dragging) return;
        const dx = x - startX;
        const dy = y - startY;
        if (!axis && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
          axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
        }
        if (axis !== 'x') return;
        moved = true;
        track.classList.add('is-dragging');
        track.scrollLeft = startScroll - dx;
        if (event && event.cancelable) event.preventDefault();
      };

      const onEnd = () => {
        if (moved) {
          const suppress = (event) => {
            event.preventDefault();
            event.stopPropagation();
            track.removeEventListener('click', suppress, true);
          };
          track.addEventListener('click', suppress, true);
          setTimeout(() => track.removeEventListener('click', suppress, true), 50);
        }
        dragging = false;
        moved = false;
        axis = null;
        track.classList.remove('is-dragging');
        const size = slideSize();
        if (size > 0) {
          const index = Math.round(track.scrollLeft / size);
          track.scrollTo({ left: index * size, behavior: 'smooth' });
        }
        updateArrows();
      };

      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('button, form, .cart-drawer__pair-well-with-arrow')) return;
        onStart(event.clientX, event.clientY);
      });
      track.addEventListener('pointermove', (event) => onMove(event.clientX, event.clientY, event));
      track.addEventListener('pointerup', onEnd);
      track.addEventListener('pointercancel', onEnd);
      track.addEventListener('pointerleave', () => {
        if (dragging) onEnd();
      });

      track.addEventListener('touchstart', (event) => {
        if (event.target.closest('button, form, .cart-drawer__pair-well-with-arrow')) return;
        const touch = event.changedTouches[0];
        onStart(touch.clientX, touch.clientY);
      }, { passive: true });
      track.addEventListener('touchmove', (event) => {
        const touch = event.changedTouches[0];
        onMove(touch.clientX, touch.clientY, event);
      }, { passive: false });
      track.addEventListener('touchend', onEnd);
      track.addEventListener('touchcancel', onEnd);
    });
  }

  bindIosScrollbar() {
    if (!cartDrawerIsIos()) return;
    const scroll = this.querySelector('.cart-drawer__scroll');
    const thumb = this.querySelector('.cart-drawer__ios-scrollbar-thumb');
    if (!scroll || !thumb || scroll.dataset.iosSbBound) return;

    scroll.dataset.iosSbBound = 'true';
    const update = () => {
      const view = scroll.clientHeight;
      const total = scroll.scrollHeight;
      const track = thumb.parentElement;
      if (!track || total <= view) {
        track && (track.style.opacity = '0');
        return;
      }
      track.style.opacity = '1';
      const trackH = track.clientHeight;
      const thumbH = Math.max(36, (view / total) * trackH);
      const maxTop = trackH - thumbH;
      const top = maxTop * (scroll.scrollTop / (total - view));
      thumb.style.height = `${thumbH}px`;
      thumb.style.transform = `translateY(${top}px)`;
    };

    scroll.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(update).observe(scroll);
    update();
    requestAnimationFrame(update);
  }

  setHeaderCartIconAccessibility() {
    if (window.__cartDrawerV2IconBound) return;
    window.__cartDrawerV2IconBound = true;

    const openFromIcon = (event) => {
      const cartLink = event.target.closest && event.target.closest('#cart-icon-bubble');
      if (!cartLink) return;
      event.preventDefault();
      const drawer = document.querySelector('cart-drawer-v2');
      if (!drawer || typeof drawer.open !== 'function') return;
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      drawer.open(cartLink);
    };

    document.addEventListener('click', openFromIcon, true);
    document.addEventListener('keydown', (event) => {
      if (event.code !== 'Space') return;
      openFromIcon(event);
    }, true);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.querySelector('#CartDrawerV2') || this.querySelector('.drawer__inner');
      const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const sections = parsedState && parsedState.sections;
    if (!sections || !sections['cart-drawer-v2']) {
      this.refreshAndOpen();
      return;
    }

    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      if (!sections[section.id]) return;
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const html = this.getSectionInnerHTML(sections[section.id], section.selector);
      if (!sectionElement || html == null) return;
      sectionElement.innerHTML = html;
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawerV2-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.bindIosScrollbar();
      this.bindPairWellSliders();
      if (window.cartDrawerV2Watch) window.cartDrawerV2Watch(this);
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer-v2',
        selector: '#CartDrawerV2'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  refreshAndOpen(triggeredBy) {
    const drawer = this;
    fetch(window.location.pathname + '?sections=cart-drawer-v2,cart-icon-bubble')
      .then((response) => response.json())
      .then((data) => {
        const html = data['cart-drawer-v2'];
        if (html) {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const next = parsed.querySelector('#CartDrawerV2');
          const current = drawer.querySelector('#CartDrawerV2');
          if (next && current) current.innerHTML = next.innerHTML;
        }
        const bubbleHtml = data['cart-icon-bubble'];
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && bubbleHtml) {
          const parsedBubble = new DOMParser().parseFromString(bubbleHtml, 'text/html');
          const nextBubble = parsedBubble.querySelector('.shopify-section') || parsedBubble.body;
          if (nextBubble) bubble.innerHTML = nextBubble.innerHTML;
        }
        drawer.classList.toggle('is-empty', !drawer.querySelector('.cart-item'));
        const overlay = drawer.querySelector('#CartDrawerV2-Overlay');
        if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
        drawer.bindIosScrollbar();
        drawer.bindPairWellSliders();
        if (window.cartDrawerV2Watch) window.cartDrawerV2Watch(drawer);
        drawer.open(triggeredBy);
      })
      .catch((error) => {
        console.error('Cart drawer v2 refresh:', error);
      });
  }
}

customElements.define('cart-drawer-v2', CartDrawerV2);

(function () {
  function keepRemoveButtonsEnabled(drawer) {
    drawer.querySelectorAll('cart-remove-button').forEach(function (remove) {
      var button = remove.querySelector('button');
      if (button) button.disabled = false;
      if (remove.dataset.v2Remove === 'true') return;
      remove.dataset.v2Remove = 'true';
      remove.addEventListener('mouseleave', function () {
        var innerButton = remove.querySelector('button');
        if (innerButton) innerButton.disabled = false;
      });
    });
  }

  window.cartDrawerV2Watch = function (drawer) {
    var inner = drawer.querySelector('.drawer__inner');
    keepRemoveButtonsEnabled(drawer);
    if (!inner || inner.dataset.v2Watch === 'true') return;
    inner.dataset.v2Watch = 'true';
    new MutationObserver(function () {
      if (typeof drawer.bindIosScrollbar === 'function') drawer.bindIosScrollbar();
      if (typeof drawer.bindPairWellSliders === 'function') drawer.bindPairWellSliders();
      keepRemoveButtonsEnabled(drawer);
      drawer.classList.toggle('is-empty', !drawer.querySelector('.cart-item'));
    }).observe(inner, { childList: true });
  };

  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest('cart-drawer-v2 .quantity__button');
      if (!button || button.disabled) return;
      var qty = button.closest('quantity-input');
      if (!qty) return;
      var input = qty.querySelector('input');
      if (!input) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      var previousValue = input.value;
      if (button.name === 'plus') input.stepUp();
      else input.stepDown();
      if (previousValue !== input.value) input.dispatchEvent(new Event('change', { bubbles: true }));
    },
    true
  );

  customElements.whenDefined('cart-drawer').then(function () {
    if (typeof CartDrawerItems === 'undefined' || !CartDrawerItems.prototype || CartDrawerItems.prototype.__v2Sections) return;
    CartDrawerItems.prototype.__v2Sections = true;
    var origSections = CartDrawerItems.prototype.getSectionsToRender;
    CartDrawerItems.prototype.getSectionsToRender = function () {
      if (this.closest('cart-drawer-v2')) {
        return [
          {
            id: 'CartDrawerV2',
            section: 'cart-drawer-v2',
            selector: '.drawer__inner'
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section'
          }
        ];
      }
      return origSections.call(this);
    };
  });
})();
